// components/create/StyleSection.js
//
// Theme, font, text colour and effect — the row of four circles the app puts
// directly under the cover.
//
// ── Why this exists ────────────────────────────────────────────────────────
//
// The website's create form never wrote `theme_type`, `custom_theme_color`,
// `font_family`, `text_color`, `effect_id` or `effect_layer`. The invitation
// PAGE has read and rendered all six for a while, so an invitation made in the
// app arrives styled and the same invitation made on the web arrives plain —
// same product, visibly different result depending on where you made it.
//
// ── Text colour is automatic by default ────────────────────────────────────
//
// White text on a pale theme is unreadable, and that is measurable rather than
// a matter of taste: `pickTextColor` in shared/eventPalette compares the real
// contrast ratio of white and near-black against the chosen ground and returns
// the winner. So the default is null — meaning "decide at render time" — and
// changing the theme later moves the text with it. A host who wants gold on
// maroon can still say so; that is a choice, not a mistake.

import React, { useState, useEffect } from 'react';
import { FiX, FiMusic } from 'react-icons/fi';
import MusicPickerSheet from '../MusicPickerSheet';
import { BACKGROUND_MUSIC_ENABLED } from '../../shared/backgroundMusic';
import { INVITATION_MUSIC_ENABLED, INVITATION_MUSIC_LABEL } from '../../shared/invitationMusic';
import InvitationMusicPicker from './InvitationMusicPicker';
import supabase from '../../services/supabase';
import { pickTextColor } from '../../shared/eventPalette';
// The site's own font table, already used by the greeting-card editor and
// already backed by @font-face blocks in global.scss. Same ids as the app's
// constants/cardFonts.js, because font_family is a shared column.
import { CARD_FONTS, getFontById } from '../../constants/cardFonts';
import '../../styles/StyleSection.scss';
import Icon from '../Icon';
import Portal from '../Portal';

/** The app's ThemeSelector palette, unchanged so both surfaces offer the same. */
const PRESET_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A',
  '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2',
  '#F8B88B', '#ABEBC6', '#FAD7A0', '#D7BDE2',
];

/** Where an effect is drawn relative to the invitation's own controls. */
const EFFECT_LAYERS = [
  { id: 'above', title: 'Over everything', hint: 'Drifts over the buttons too. Taps still go through.' },
  // 'content' is the stored value for "behind" (effect_layer), kept from when
  // it meant over the content but under the buttons.
  { id: 'content', title: 'Behind content', hint: 'Plays on the background, behind the cover, text and buttons.' },
];

function Sheet({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <Portal>
    <div className="style-sheet-scrim" onClick={onClose}>
      <div
        className="style-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="style-sheet-head">
          <h2>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Done"><FiX /></button>
        </div>
        <div className="style-sheet-body">{children}</div>
      </div>
    </div>
    </Portal>
  );
}

export default function StyleSection({
  theme, setTheme,
  font, setFont,
  textColor, setTextColor,
  effectId, setEffectId,
  effectLayer, setEffectLayer,
  backgroundMusic, setBackgroundMusic,
  invitationMusic, setInvitationMusic,
  eventType,
  coverImageUri,
}) {
  const [open, setOpen] = useState(null); // 'theme' | 'font' | 'text' | 'effect' | 'music' | 'invitation-music'
  const offersInvitationMusic = INVITATION_MUSIC_ENABLED && !BACKGROUND_MUSIC_ENABLED && !!setInvitationMusic;

  const [videoThemes, setVideoThemes] = useState([]);
  const [effects, setEffects] = useState([]);

  useEffect(() => {
    if (open !== 'theme' || videoThemes.length) return undefined;
    let cancelled = false;
    supabase
      .from('themes')
      .select('id, theme_name, thumbnail_cdn_url, video_cdn_url, default_color')
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data }) => { if (!cancelled) setVideoThemes(data ?? []); });
    return () => { cancelled = true; };
  }, [open, videoThemes.length]);

  useEffect(() => {
    if (open !== 'effect' || effects.length) return undefined;
    let cancelled = false;
    supabase
      .from('invitation_effects')
      .select('id, name, emoji, mobile_url, web_url, is_active')
      .order('sort_order', { ascending: true })
      .then(({ data }) => { if (!cancelled) setEffects(data ?? []); });
    return () => { cancelled = true; };
  }, [open, effects.length]);

  // The ground the text will actually sit on, so the Aa swatch previews the
  // real answer rather than a generic glyph.
  const themeGround = theme?.type === 'color'
    ? theme.value
    : theme?.type === 'video'
      ? (theme.defaultColor || '#1a1a2e')
      : '#0B0B0F';

  // pickTextColor returns { primary, secondary, muted }; the swatch wants the
  // primary, which is the colour a title would be drawn in.
  const resolvedText = textColor || pickTextColor(themeGround).primary;
  const fontMeta = getFontById(font);

  return (
    <div className="style-section">
      <div className="style-section-label">
        <Icon name="color-palette" size={15} />
        <span>{BACKGROUND_MUSIC_ENABLED || offersInvitationMusic ? 'Theme, font, effects & music' : 'Theme, font & effects'}</span>
      </div>

      <div className="style-circles">
        {/* Theme */}
        <div className="style-circle-wrap">
          <button
            type="button"
            className="style-circle"
            onClick={() => setOpen('theme')}
            aria-label="Theme"
          >
            {theme?.type === 'image' ? (
              <span className="style-circle-fill" style={{ backgroundImage: `url(${coverImageUri})` }} />
            ) : theme?.type === 'video' ? (
              <span
                className="style-circle-fill"
                style={{ backgroundImage: `url(${theme.thumbnailCdnUrl})` }}
              >
                <Icon name="play-circle" size={18} />
              </span>
            ) : (
              <span
                className="style-circle-fill"
                style={{ background: theme?.type === 'color' ? theme.value : '#4ECDC4' }}
              />
            )}
          </button>
          <span className="style-circle-label">Theme</span>
        </div>

        {/* Font */}
        <div className="style-circle-wrap">
          <button
            type="button"
            className={`style-circle ${font && font !== 'default' ? 'is-set' : ''}`}
            onClick={() => setOpen('font')}
            aria-label="Font"
          >
            <span style={{ fontFamily: fontMeta.fontFamily, fontSize: 21 }}>Aa</span>
          </button>
          <span className="style-circle-label">
            {font && font !== 'default' ? fontMeta.name : 'Font'}
          </span>
        </div>

        {/* Text colour */}
        <div className="style-circle-wrap">
          <button
            type="button"
            className={`style-circle ${textColor ? 'is-set' : ''}`}
            onClick={() => setOpen('text')}
            aria-label={textColor ? 'Text colour, custom' : 'Text colour, automatic'}
          >
            <span className="style-circle-fill" style={{ background: themeGround }}>
              <span style={{ color: resolvedText, fontSize: 15, fontWeight: 700 }}>Aa</span>
            </span>
          </button>
          <span className="style-circle-label">{textColor ? 'Text' : 'Text: Auto'}</span>
        </div>

        {/* Effect */}
        <div className="style-circle-wrap">
          <button
            type="button"
            className={`style-circle ${effectId ? 'is-set' : ''}`}
            onClick={() => setOpen('effect')}
            aria-label={effectId ? 'Effect, chosen' : 'Effect, none'}
          >
            <Icon name="sparkles" size={20} />
          </button>
          <span className="style-circle-label">
            {effectId ? (effects.find((e) => e.id === effectId)?.name ?? 'Effect') : 'Effect'}
          </span>
        </div>

        {/* Music: a looping part of a track behind the invitation. Hidden
            until a licensed provider is in place. */}
        {BACKGROUND_MUSIC_ENABLED && setBackgroundMusic && (
          <div className="style-circle-wrap">
            <button
              type="button"
              className={`style-circle ${backgroundMusic ? 'is-set' : ''}`}
              onClick={() => setOpen('music')}
              aria-label={backgroundMusic ? `Music, ${backgroundMusic.title}` : 'Music, none'}
            >
              <FiMusic size={20} />
            </button>
            <span className="style-circle-label">
              {backgroundMusic ? backgroundMusic.title : 'Music'}
            </span>
          </div>
        )}

        {/* Invitation music: a YouTube song for the end of the invitation.
            One Music circle - the licensed background track above takes its
            place if it is ever switched on. */}
        {offersInvitationMusic && (
          <div className="style-circle-wrap">
            <button
              type="button"
              className={`style-circle ${invitationMusic ? 'is-set' : ''}`}
              onClick={() => setOpen('invitation-music')}
              aria-label={invitationMusic ? `${INVITATION_MUSIC_LABEL}, ${invitationMusic.title}` : `${INVITATION_MUSIC_LABEL}, none`}
            >
              <FiMusic size={20} />
            </button>
            <span className="style-circle-label">
              {invitationMusic ? invitationMusic.title : 'Music'}
            </span>
          </div>
        )}
      </div>

      {open === 'invitation-music' && (
        <Sheet title={INVITATION_MUSIC_LABEL} onClose={() => setOpen(null)}>
          <InvitationMusicPicker
            value={invitationMusic}
            onChange={setInvitationMusic}
            eventType={eventType}
            onPicked={() => setOpen(null)}
          />
        </Sheet>
      )}

      {open === 'music' && (
        <MusicPickerSheet
          onClose={() => setOpen(null)}
          value={backgroundMusic}
          onChange={setBackgroundMusic}
          eventType={eventType}
        />
      )}

      {/* ── Theme sheet ─────────────────────────────────────────────────── */}
      {open === 'theme' && (
        <Sheet title="Theme" onClose={() => setOpen(null)}>
          <h3 className="style-sheet-title">Colour</h3>
          <div className="style-swatches">
            {PRESET_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`style-swatch ${theme?.type === 'color' && theme.value === color ? 'is-active' : ''}`}
                style={{ background: color }}
                onClick={() => setTheme({ type: 'color', value: color })}
                aria-label={`Colour ${color}`}
              />
            ))}

            {/* The native colour input rather than a wheel widget: it is one
                element, it is accessible, and on a phone it opens the system
                picker people already know. */}
            <label className="style-swatch style-swatch--custom">
              <input
                type="color"
                value={theme?.type === 'color' ? theme.value : '#4ECDC4'}
                onChange={(e) => setTheme({ type: 'color', value: e.target.value })}
                aria-label="Custom colour"
              />
              <Icon name="add" size={16} />
            </label>
          </div>

          {coverImageUri && (
            <>
              <h3 className="style-sheet-title">From the cover</h3>
              <button
                type="button"
                className={`style-theme-card ${theme?.type === 'image' ? 'is-active' : ''}`}
                onClick={() => setTheme({ type: 'image' })}
              >
                <img src={coverImageUri} alt="" />
                <span>Use the cover image</span>
              </button>
            </>
          )}

          <h3 className="style-sheet-title">Animated</h3>
          {videoThemes.length === 0 ? (
            <p className="style-sheet-note">Loading themes…</p>
          ) : (
            <div className="style-theme-grid">
              {videoThemes.map((videoTheme) => (
                <button
                  key={videoTheme.id}
                  type="button"
                  className={`style-theme-tile ${theme?.id === videoTheme.id ? 'is-active' : ''}`}
                  onClick={() => setTheme({
                    type: 'video',
                    id: videoTheme.id,
                    themeName: videoTheme.theme_name,
                    videoCdnUrl: videoTheme.video_cdn_url,
                    thumbnailCdnUrl: videoTheme.thumbnail_cdn_url,
                    defaultColor: videoTheme.default_color || '#1a1a2e',
                  })}
                >
                  <img src={videoTheme.thumbnail_cdn_url} alt="" loading="lazy" />
                  <span>{videoTheme.theme_name}</span>
                </button>
              ))}
            </div>
          )}
        </Sheet>
      )}

      {/* ── Font sheet ──────────────────────────────────────────────────── */}
      {open === 'font' && (
        <Sheet title="Font" onClose={() => setOpen(null)}>
          <div className="style-font-list">
            {CARD_FONTS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`style-font-row ${font === f.id ? 'is-active' : ''}`}
                onClick={() => setFont(f.id)}
              >
                <span className="style-font-sample" style={{ fontFamily: f.fontFamily }}>
                  {/* The name in its own face, which is the only preview that
                      tells you anything. */}
                  {f.name}
                </span>
                {font === f.id && <Icon name="checkmark-circle" size={18} />}
              </button>
            ))}
          </div>
        </Sheet>
      )}

      {/* ── Text colour sheet ───────────────────────────────────────────── */}
      {open === 'text' && (
        <Sheet title="Text colour" onClose={() => setOpen(null)}>
          <div className="style-text-preview" style={{ background: themeGround }}>
            <span style={{ color: resolvedText }}>The quick brown fox</span>
          </div>

          <button
            type="button"
            className={`style-option ${!textColor ? 'is-active' : ''}`}
            onClick={() => setTextColor(null)}
          >
            <span>
              <strong>Automatic</strong>
              <small>Picks whichever of white or near-black is readable on your theme.</small>
            </span>
            {!textColor && <Icon name="checkmark-circle" size={18} />}
          </button>

          <div className="style-swatches">
            {['#FFFFFF', '#0B0B0F', '#FFD700', '#FF6B6B', '#4ECDC4'].map((color) => (
              <button
                key={color}
                type="button"
                className={`style-swatch ${textColor === color ? 'is-active' : ''}`}
                style={{ background: color }}
                onClick={() => setTextColor(color)}
                aria-label={`Text colour ${color}`}
              />
            ))}
            <label className="style-swatch style-swatch--custom">
              <input
                type="color"
                value={textColor || '#FFFFFF'}
                onChange={(e) => setTextColor(e.target.value)}
                aria-label="Custom text colour"
              />
              <Icon name="add" size={16} />
            </label>
          </div>
        </Sheet>
      )}

      {/* ── Effect sheet ────────────────────────────────────────────────── */}
      {open === 'effect' && (
        <Sheet title="Effect" onClose={() => setOpen(null)}>
          <div className="style-effect-grid">
            <button
              type="button"
              className={`style-effect-tile ${!effectId ? 'is-active' : ''}`}
              onClick={() => setEffectId(null)}
            >
              <span className="style-effect-glyph"><Icon name="close-circle" size={22} /></span>
              <span>None</span>
            </button>

            {/* Inactive effects stay playable on invitations that already have
                them, but are not offered for new ones. */}
            {effects
              .filter((e) => e.is_active !== false || e.id === effectId)
              .map((e) => (
                <button
                  key={e.id}
                  type="button"
                  className={`style-effect-tile ${effectId === e.id ? 'is-active' : ''}`}
                  onClick={() => setEffectId(e.id)}
                >
                  <span className="style-effect-glyph" aria-hidden="true">{e.emoji || '✨'}</span>
                  <span>{e.name}</span>
                </button>
              ))}
          </div>

          {effectId && (
            <>
              <h3 className="style-sheet-title">Show it</h3>
              {EFFECT_LAYERS.map((layer) => (
                <button
                  key={layer.id}
                  type="button"
                  className={`style-option ${effectLayer === layer.id ? 'is-active' : ''}`}
                  onClick={() => setEffectLayer(layer.id)}
                >
                  <span>
                    <strong>{layer.title}</strong>
                    <small>{layer.hint}</small>
                  </span>
                  {effectLayer === layer.id && <Icon name="checkmark-circle" size={18} />}
                </button>
              ))}
            </>
          )}
        </Sheet>
      )}
    </div>
  );
}
